import { useContext } from 'react';
import { useApiClient } from '@m43/core';
import { RelatedDataResource, ApiResultRelatedDataView } from '@mark43/cad-api';
import pRetry from 'p-retry';
import { NewEventFields, RelatedDataType } from './types';
import { RelatedDataStoreContext } from './related-data-provider';
import { RelatedDataStore } from './related-data-store';
import { AxiosResponse } from 'axios-cad-nx';
import {
    buildFromFieldsForRequestData,
    determineTypesToReplaceOrUpsert,
    determineTypesToUpdateNextFrom,
    mapNewEventFieldNamesForRequest,
} from './utils';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

const setIsLoading = (isLoading: boolean, store: RelatedDataStore, isViewMoreRequest: boolean) => {
    if (isViewMoreRequest) {
        store.setIsLoadingViewMore(isLoading);
    } else {
        store.setIsLoadingPoll(isLoading);
    }
};

/*
    Behavior when updating the data store with fetched related data entities and their nextFrom fields changes
    after the user interacts with the 'View More' button for each entity type (cad events, rms reports, vehicles):

    - Before 'View More' is clicked:
        * Poll requests should replace items in the associated list store (not upsert).
        * Poll requests should update the corresponding nextFrom field in the store.

          Example: If 'View More' has not been clicked for Cad Events, poll requests will replace items in
          the cadEventsStore and update the cadEventsNextFrom field.

    - After 'View More' is clicked:
        * Polling should cease for that entity type, and responses from polls should not update the list store.
        * 'View More' requests should upsert items into the associated list store.
        * 'View More' requests should update the nextFrom field in the store.

          Example: If 'View More' has been clicked for Cad Events, poll requests should not alter the cadEventsNextFrom field.
          Instead, the response from 'View More' requests should update the cadEventsNextFrom field.

    Rationale:
    Once 'View More' is clicked, polling for related data should stop to prevent overwriting data that might have been
    loaded through the 'View More' action. It’s crucial that the nextFrom field is updated using the response to the
    'View More' request to ensure the correct sequence of items is maintained, avoiding incorrect indexing that would
    occur if using the item after a poll response rather than the one after the 'View More' response.
*/

const executeGetRelatedDataRequest = async (options: {
    getRelatedDataRequest: () => Promise<AxiosResponse<ApiResultRelatedDataView, unknown>>;
    typesToUpdate?: RelatedDataType[];
    isViewMoreRequest: boolean;
    store: RelatedDataStore;
    enhancedRelatedDataEnabled: boolean;
}) => {
    const {
        getRelatedDataRequest,
        typesToUpdate = ['CAD_EVENTS', 'RMS_REPORTS', 'VEHICLES'],
        isViewMoreRequest,
        store,
        enhancedRelatedDataEnabled,
    } = options;

    setIsLoading(true, store, isViewMoreRequest);

    try {
        const response = await getRelatedDataRequest();
        const relatedDataView = response.data.data;

        if (relatedDataView === undefined) {
            return;
        }

        const typesToUpdateNextFrom = determineTypesToUpdateNextFrom(
            typesToUpdate,
            isViewMoreRequest,
            store
        );

        const { typesToReplace, typesToUpsert } = determineTypesToReplaceOrUpsert(
            typesToUpdate,
            isViewMoreRequest,
            store,
            enhancedRelatedDataEnabled
        );

        store.update({
            relatedDataView,
            typesToReplace,
            typesToUpsert,
            typesToUpdateNextFrom,
        });

        return relatedDataView;
    } finally {
        setIsLoading(false, store, isViewMoreRequest);
    }
};

interface FetchRelatedDataOptionsBase {
    isNewEvent: boolean;
    typesToUpdate?: RelatedDataType[];
    updateNextFrom?: boolean;
    cadEventsFrom?: number;
    rmsReportsFrom?: number;
    vehiclesFrom?: number;
    retries?: number;
}

interface FetchRelatedDataForNewEventOptions extends FetchRelatedDataOptionsBase {
    isNewEvent: true;
    newEventFields: NewEventFields;
}

interface FetchRelatedDataForExistingEventOptions extends FetchRelatedDataOptionsBase {
    isNewEvent: false;
}

type FetchRelatedDataOptions =
    | FetchRelatedDataForNewEventOptions
    | FetchRelatedDataForExistingEventOptions;

export const useRelatedData = () => {
    const store = useContext(RelatedDataStoreContext);
    if (!store) throw new Error("Couldn't find RelatedDataStoreContext");

    const apiClient = useApiClient();
    const { enhancedRelatedData: enhancedRelatedDataEnabled } = useEnabledFeatures();

    const fetchRelatedData = async (options: FetchRelatedDataOptions) => {
        const {
            isNewEvent,
            typesToUpdate = ['CAD_EVENTS', 'RMS_REPORTS', 'VEHICLES'],
            cadEventsFrom,
            rmsReportsFrom,
            vehiclesFrom,
            retries = 3,
        } = options;

        const fromFields = buildFromFieldsForRequestData(
            cadEventsFrom,
            rmsReportsFrom,
            vehiclesFrom
        );

        const getRelatedDataRequest = async () => {
            return await pRetry(
                () => {
                    if (isNewEvent) {
                        const newEventFields = mapNewEventFieldNamesForRequest(
                            options.newEventFields
                        );

                        return apiClient.request<RelatedDataResource.GetRelatedDataByQuery>({
                            method: 'POST',
                            url: `related_data/`,
                            data: {
                                ...newEventFields,
                                ...(enhancedRelatedDataEnabled && fromFields ? fromFields : {}),
                                limitSearchResultSize: true,
                            },
                        });
                    } else {
                        return enhancedRelatedDataEnabled
                            ? apiClient.request<RelatedDataResource.GetRelatedDataByEventIdWithQuery>(
                                  {
                                      method: 'POST',
                                      url: `related_data/${store.eventId}/`,
                                      ...(fromFields
                                          ? {
                                                data: {
                                                    ...fromFields,
                                                    personQueries: [],
                                                    phoneNumbers: [],
                                                    vehicleQueries: [],
                                                },
                                            }
                                          : {}),
                                  }
                              )
                            : apiClient.request<RelatedDataResource.GetRelatedDataByEventId>({
                                  method: 'GET',
                                  url: `related_data/${store.eventId}/`,
                              });
                    }
                },
                { retries }
            );
        };

        return await executeGetRelatedDataRequest({
            isViewMoreRequest: fromFields !== undefined,
            getRelatedDataRequest,
            typesToUpdate,
            store,
            enhancedRelatedDataEnabled,
        });
    };

    type ViewMoreItemsOptions =
        | {
              isNewEvent: true;
              newEventFields: NewEventFields;
          }
        | {
              isNewEvent: false;
          };

    /**
     * Note: There's a known and currently accepted issue with how pagination is handled for 'View More' requests.
     *
     * Problem Description:
     * - The `nextFrom` pagination index used by the API is based on the most recent related data entity. As new
     *   entities are continuously added, this index can become stale, leading to situations where the API call
     *   for 'View More' might return entities that the user already has, resulting in no updates to the UI.
     *
     * Potential Solutions:
     * - Introduce a `fromDirection` field with values `DESC/ASC` in the API. This would allow clients to request
     *   that the API consider `from` indices based on the oldest related data event, reducing the likelihood of
     *   staleness. However, if the oldest entities are modified or become unrelated, this could still result in
     *   the `from` index being invalid.
     *
     * - Introduce a `fromEntityId` field in the API that allows fetching entities older than a specific ID. This
     *   approach ensures that only older entities are returned, minimizing stale data issues. However, as long as
     *   the list is not being updated by websocket, the client data still won't be perfect.
     *
     * We could also emit a Sentry event to track when users click the 'View More' button without the UI updating
     * to determine how widespread this issue is.
     */
    const viewMoreRelatedData = async (
        options: ViewMoreItemsOptions,
        relatedDataType: RelatedDataType
    ) => {
        const relatedDataTypeConfig = {
            CAD_EVENTS: {
                fromField: 'cadEventsFrom',
                nextFrom: store.cadEventsNextFrom,
            },
            RMS_REPORTS: {
                fromField: 'rmsReportsFrom',
                nextFrom: store.rmsReportsNextFrom,
            },
            VEHICLES: {
                fromField: 'vehiclesFrom',
                nextFrom: store.vehiclesNextFrom,
            },
        };

        const config = relatedDataTypeConfig[relatedDataType];

        if (options.isNewEvent) {
            await fetchRelatedData({
                isNewEvent: true,
                newEventFields: options.newEventFields,
                typesToUpdate: [relatedDataType],
                [config.fromField]: config.nextFrom,
            });
        } else {
            await fetchRelatedData({
                isNewEvent: false,
                typesToUpdate: [relatedDataType],
                [config.fromField]: config.nextFrom,
            });
        }
    };

    const viewMoreCadEvents = async (options: ViewMoreItemsOptions) => {
        store.setIsCadEventsPollEnabled(false);
        await viewMoreRelatedData(options, 'CAD_EVENTS');
    };

    const viewMoreRmsReports = async (options: ViewMoreItemsOptions) => {
        store.setIsRmsReportsPollEnabled(false);
        await viewMoreRelatedData(options, 'RMS_REPORTS');
    };

    const viewMoreVehicles = async (options: ViewMoreItemsOptions) => {
        store.setIsVehiclesPollEnabled(false);
        await viewMoreRelatedData(options, 'VEHICLES');
    };

    return {
        cadEvents: store.cadEvents,
        cadEventsCount: store.cadEventsCount,
        fetchRelatedData,
        hasMoreCadEvents: store.hasMoreCadEvents,
        hasMoreRmsReports: store.hasMoreRmsReports,
        hasMoreVehicles: store.hasMoreVehicles,
        isLoadingPoll: store.isLoadingPoll,
        isLoadingViewMore: store.isLoadingViewMore,
        isPollEnabled: store.isPollEnabled,
        isCadEventsPollEnabled: store.isCadEventsPollEnabled,
        isRmsReportsPollEnabled: store.isRmsReportsPollEnabled,
        isVehiclesPollEnabled: store.isVehiclesPollEnabled,
        rmsReports: store.rmsReports,
        rmsReportsCount: store.rmsReportsCount,
        vehicles: store.vehicles,
        vehiclesCount: store.vehiclesCount,
        viewMoreCadEvents,
        viewMoreRmsReports,
        viewMoreVehicles,
    };
};
